// Rewindable player for the live call monitor. Every µ-law byte received
// for the call is kept in memory (8 KB/s, capped at MAX_SECONDS), and a
// small scheduler feeds short AudioBuffers to Web Audio from a movable
// cursor. "Live" just means the cursor is at the end of what we've received;
// rewinding moves it back, and playback naturally runs forward from there
// while new audio keeps accumulating behind it.

const SAMPLE_RATE = 8000;
const MAX_SECONDS = 60 * 60;
const SCHEDULE_AHEAD_S = 0.3;
const PIECE_SAMPLES = SAMPLE_RATE / 10; // 100 ms per scheduled buffer
const LIVE_LEAD_S = 0.25; // jitter cushion when jumping to live
const LIVE_THRESHOLD_S = 1.5;

const DECODE = (() => {
  const t = new Float32Array(256);
  for (let i = 0; i < 256; i++) {
    const u = ~i & 0xff;
    const exponent = (u >> 4) & 0x07;
    const sample = ((((u & 0x0f) << 3) + 0x84) << exponent) - 0x84;
    t[i] = (u & 0x80 ? -sample : sample) / 32768;
  }
  return t;
})();

interface Scheduled {
  src: AudioBufferSourceNode;
  startTime: number;
  from: number;
  length: number;
}

export interface MonitorPlayerState {
  /** Seconds of audio held locally. */
  bufferedSeconds: number;
  /** Playback position, in seconds from the start of the local buffer. */
  positionSeconds: number;
  /** How far playback is behind the newest audio. */
  behindSeconds: number;
  isLive: boolean;
  paused: boolean;
  /** Recent signal level 0..1 at the playback position (for a meter). */
  level: number;
}

export class MonitorPlayer {
  private ctx: AudioContext;
  private gain: GainNode;
  private data = new Uint8Array(SAMPLE_RATE * 60);
  private length = 0;
  private baseOffset: number | null = null; // absolute sample index of data[0]
  private cursor = 0; // next index in data to schedule
  private nextTime = 0;
  private scheduled: Scheduled[] = [];
  private timer: ReturnType<typeof setInterval>;
  private followLive = true;
  private _paused = false;

  constructor() {
    this.ctx = new AudioContext();
    this.gain = this.ctx.createGain();
    this.gain.connect(this.ctx.destination);
    this.timer = setInterval(() => this.pump(), 50);
  }

  /** Must be called from a user gesture on some browsers before audio plays. */
  async resume(): Promise<void> {
    if (this.ctx.state === 'suspended') await this.ctx.resume();
  }

  setVolume(v: number): void {
    this.gain.gain.value = Math.max(0, Math.min(1, v));
  }

  append(offset: number, bytes: Uint8Array): void {
    if (this.baseOffset === null) this.baseOffset = offset;
    const expected = this.baseOffset + this.length;
    let src = bytes;
    if (offset < expected) {
      // Overlap (e.g. backlog re-sent after a reconnect): keep only the new tail.
      const skip = expected - offset;
      if (skip >= src.length) return;
      src = src.subarray(skip);
    } else if (offset > expected) {
      // Gap: pad with silence so positions stay aligned with the call timeline.
      this.write(new Uint8Array(Math.min(offset - expected, SAMPLE_RATE * 60)).fill(0xff));
    }
    this.write(src);
  }

  private write(bytes: Uint8Array): void {
    if (this.length + bytes.length > this.data.length) {
      const grown = new Uint8Array(Math.max(this.data.length * 2, this.length + bytes.length));
      grown.set(this.data.subarray(0, this.length));
      this.data = grown;
    }
    this.data.set(bytes, this.length);
    this.length += bytes.length;

    const overflow = this.length - MAX_SECONDS * SAMPLE_RATE;
    if (overflow > 0) {
      this.data.copyWithin(0, overflow, this.length);
      this.length -= overflow;
      this.baseOffset = (this.baseOffset ?? 0) + overflow;
      this.cursor = Math.max(0, this.cursor - overflow);
      for (const s of this.scheduled) s.from -= overflow;
    }
  }

  private pump(): void {
    const now = this.ctx.currentTime;
    this.scheduled = this.scheduled.filter((s) => s.startTime + s.length / SAMPLE_RATE > now);
    if (this._paused) return;

    // If we fell badly behind while "live" (e.g. tab throttled), snap back.
    if (this.followLive && (this.length - this.cursor) / SAMPLE_RATE > LIVE_THRESHOLD_S + 1) {
      this.seekToIndex(this.length - LIVE_LEAD_S * SAMPLE_RATE);
      return;
    }

    while (this.nextTime - now < SCHEDULE_AHEAD_S && this.cursor < this.length) {
      const n = Math.min(PIECE_SAMPLES, this.length - this.cursor);
      // At the live edge, wait for a sensible chunk rather than scheduling slivers.
      if (n < PIECE_SAMPLES / 2 && this.nextTime > now + 0.05) break;
      const buf = this.ctx.createBuffer(1, n, SAMPLE_RATE);
      const ch = buf.getChannelData(0);
      for (let i = 0; i < n; i++) ch[i] = DECODE[this.data[this.cursor + i]!]!;
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      src.connect(this.gain);
      // Underrun: restart with a small cushion instead of playing in the past.
      if (this.nextTime < now + 0.02) this.nextTime = now + 0.08;
      src.start(this.nextTime);
      this.scheduled.push({ src, startTime: this.nextTime, from: this.cursor, length: n });
      this.nextTime += n / SAMPLE_RATE;
      this.cursor += n;
    }
  }

  private playingIndex(): number {
    const now = this.ctx.currentTime;
    for (const s of this.scheduled) {
      if (now >= s.startTime && now < s.startTime + s.length / SAMPLE_RATE) {
        return s.from + Math.floor((now - s.startTime) * SAMPLE_RATE);
      }
    }
    const first = this.scheduled[0];
    return first ? first.from : this.cursor;
  }

  private seekToIndex(index: number): void {
    for (const s of this.scheduled) {
      try { s.src.stop(); } catch { /* already stopped */ }
      s.src.disconnect();
    }
    this.scheduled = [];
    this.cursor = Math.max(0, Math.min(this.length, Math.floor(index)));
    this.nextTime = this.ctx.currentTime + 0.05;
    this.pump();
  }

  /** Jump to a position (seconds from the start of the local buffer). */
  seek(seconds: number): void {
    const index = seconds * SAMPLE_RATE;
    this.followLive = (this.length - index) / SAMPLE_RATE < LIVE_THRESHOLD_S;
    this.seekToIndex(this.followLive ? this.length - LIVE_LEAD_S * SAMPLE_RATE : index);
  }

  /** Move relative to the current playback position (negative = rewind). */
  skip(deltaSeconds: number): void {
    this.seek(this.playingIndex() / SAMPLE_RATE + deltaSeconds);
  }

  goLive(): void {
    this.followLive = true;
    if (this._paused) this.setPaused(false);
    this.seekToIndex(this.length - LIVE_LEAD_S * SAMPLE_RATE);
  }

  setPaused(paused: boolean): void {
    if (paused === this._paused) return;
    this._paused = paused;
    if (paused) {
      // Pausing takes you off live; resuming continues from the same spot.
      const at = this.playingIndex();
      this.followLive = false;
      this.seekToIndex(at);
    } else {
      this.seekToIndex(this.cursor);
    }
  }

  getState(): MonitorPlayerState {
    const pos = this._paused ? this.cursor : this.playingIndex();
    let level = 0;
    if (!this._paused && pos < this.length) {
      const end = Math.min(this.length, pos + 400);
      for (let i = pos; i < end; i++) level = Math.max(level, Math.abs(DECODE[this.data[i]!]!));
    }
    const behind = (this.length - pos) / SAMPLE_RATE;
    return {
      bufferedSeconds: this.length / SAMPLE_RATE,
      positionSeconds: pos / SAMPLE_RATE,
      behindSeconds: behind,
      isLive: this.followLive && !this._paused && behind < LIVE_THRESHOLD_S,
      paused: this._paused,
      level,
    };
  }

  destroy(): void {
    clearInterval(this.timer);
    for (const s of this.scheduled) {
      try { s.src.stop(); } catch { /* ignore */ }
    }
    this.scheduled = [];
    void this.ctx.close().catch(() => null);
  }
}
